import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useToast } from "../contexts/ToastContext";
import { Skeleton } from "../ui";
import { api } from "../lib/api";
import { formatDate, formatMoney } from "../lib/format";
import { useReceiptPhoto } from "../lib/hooks/useReceiptPhoto";
import { useBackButton } from "../lib/telegram";
import { ExpenseOut, ReceiptDetailOut } from "../lib/schemas";
import ReceiptItemCard from "../components/ReceiptItemCard";

export default function ExpenseDetail() {
  const nav = useNavigate();
  const location = useLocation();
  const { id, expenseId } = useParams<{ id: string; expenseId: string }>();
  const groupId = Number(id);
  const targetId = Number(expenseId);
  const showToast = useToast();

  const stateExpense = (location.state as { expense?: ExpenseOut } | null)?.expense;

  const [expense, setExpense] = useState<ExpenseOut | undefined>(stateExpense);
  const [loading, setLoading] = useState(!stateExpense);
  const [error, setError] = useState<string | null>(null);
  const [undoLoading, setUndoLoading] = useState(false);
  const [receiptDetail, setReceiptDetail] = useState<ReceiptDetailOut | null>(null);

  useEffect(() => {
    if (stateExpense) return;
    let cancelled = false;
    setLoading(true);
    api<ExpenseOut[]>("GET", `/chats/${groupId}/history?limit=50`)
      .then((list) => {
        if (cancelled) return;
        const found = list.find((e) => e.id === targetId);
        if (found) {
          setExpense(found);
        } else {
          setError("Expense not found");
        }
      })
      .catch((e) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load expense");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [groupId, targetId, stateExpense]);

  useEffect(() => {
    if (!expense?.source_receipt_id) return;
    let cancelled = false;
    api<ReceiptDetailOut>(
      "GET",
      `/chats/${groupId}/receipts/${expense.source_receipt_id}`,
    )
      .then((raw) => {
        if (cancelled) return;
        setReceiptDetail(ReceiptDetailOut.parse(raw));
      })
      .catch(() => { /* receipt detail is display-only; ignore errors */ });
    return () => { cancelled = true; };
  }, [expense?.source_receipt_id, groupId]);

  const photoEnabled = !!receiptDetail && receiptDetail.status === "finalized";
  const { url: photoUrl, isLoading: photoLoading } = useReceiptPhoto(
    expense?.source_receipt_id ?? undefined,
    photoEnabled,
  );

  const handleBack = useCallback(() => nav(-1), [nav]);
  useBackButton(handleBack);

  const handleUndo = useCallback(async () => {
    try {
      setUndoLoading(true);
      await api("POST", `/chats/${groupId}/undo`);
      showToast("Last expense undone", { type: "success" });
      nav(`/groups/${groupId}`, { replace: true });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Undo failed";
      showToast(msg, { type: "error" });
    } finally {
      setUndoLoading(false);
    }
  }, [groupId, nav, showToast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg px-4 pt-6 pb-8">
        <div className="bg-surface border border-hairline rounded-2xl shadow-elevated p-5 mb-4">
          <Skeleton className="h-5 w-40 mb-2" />
          <Skeleton className="h-9 w-28 mb-3" />
          <Skeleton className="h-3 w-48" />
        </div>
        <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex justify-between px-4 py-3.5 border-t border-hairline first:border-t-0">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !expense) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-danger mb-4">{error ?? "Expense not found"}</p>
          <button
            type="button"
            onClick={() => nav(`/groups/${groupId}`)}
            className="border border-fg/30 rounded-xl px-5 py-2.5 text-fg font-sans text-sm font-semibold active:opacity-70 transition-opacity"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg px-4 pt-6 pb-8">
      {/* Header */}
      <div className="bg-surface border border-hairline rounded-2xl shadow-elevated p-5 mb-4">
        <h1 className="text-base font-semibold text-fg mb-1">{expense.description}</h1>
        <p className="font-display text-5xl leading-none tracking-wide text-fg mb-3 [font-variant-numeric:tabular-nums]">
          {formatMoney(expense.total, expense.currency)}
        </p>
        <div className="flex items-center gap-2 font-sans text-xs font-medium text-muted">
          <span>Paid by {expense.payer.first_name}</span>
          <span>·</span>
          <span>{formatDate(expense.created_at)}</span>
        </div>
      </div>

      {/* Shares breakdown */}
      {expense.shares.length > 0 && (
        <div className="mb-6">
          <h2 className="font-sans text-xs font-medium text-muted mb-2.5 px-1">
            Split
          </h2>
          <div className="bg-surface border border-hairline rounded-2xl shadow-card overflow-hidden">
            {expense.shares.map((share) => (
              <div
                key={share.user.id}
                className="flex items-center justify-between px-4 py-3.5 border-t border-hairline first:border-t-0"
              >
                <span className="text-fg text-sm">{share.user.first_name}</span>
                <span className="text-fg font-medium font-mono text-sm [font-variant-numeric:tabular-nums]">
                  {formatMoney(share.amount, expense.currency)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Line items with assigned users */}
      {receiptDetail && receiptDetail.status === "finalized" && receiptDetail.items.length > 0 && (
        <div className="mb-6">
          <h2 className="font-sans text-xs font-medium text-muted mb-2.5 px-1">
            Items
          </h2>
          {receiptDetail.items.map((item) => (
            <ReceiptItemCard
              key={item.idx}
              item={{ name: item.name, price: item.price }}
              quantity={item.quantity}
              claimed={false}
              claimedBy={item.claimants}
              muted={item.muted}
              phantomCount={item.phantom_count}
              myDbId={null}
              myPortion={null}
              onToggleClaim={() => {}}
              onMute={() => {}}
              onUnmute={() => {}}
              onGuestChange={() => {}}
              onSetPortion={() => {}}
              currency={receiptDetail.currency}
              readOnly
            />
          ))}
        </div>
      )}

      {/* Receipt summary footer */}
      {receiptDetail && receiptDetail.status === "finalized" && (() => {
        const tipAmount = Number(receiptDetail.tip);
        const splitCount = receiptDetail.tip_split_count;
        return (
          <div className="mb-6">
            <h2 className="font-sans text-xs font-medium text-muted mb-2.5 px-1">
              Receipt
            </h2>
            <div className="bg-surface border border-hairline rounded-2xl shadow-card px-4 py-4 space-y-1.5">
              {Number(receiptDetail.tax) > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted">Tax (incl.)</span>
                  <span className="text-fg font-mono [font-variant-numeric:tabular-nums]">
                    {formatMoney(receiptDetail.tax, receiptDetail.currency)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">Tip</span>
                <span className="text-fg font-mono [font-variant-numeric:tabular-nums]">
                  {formatMoney(receiptDetail.tip, receiptDetail.currency)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">Split</span>
                <div className="flex items-center gap-2">
                  <span className="text-fg font-mono [font-variant-numeric:tabular-nums]">
                    {splitCount} {splitCount === 1 ? "person" : "people"}
                  </span>
                  {tipAmount > 0 && (
                    <span className="text-faint text-xs font-sans">
                      ~{formatMoney(receiptDetail.per_person_tip, receiptDetail.currency)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between text-sm font-semibold pt-1 border-t border-hairline">
                <span className="text-fg">Total</span>
                <span className="text-fg font-mono [font-variant-numeric:tabular-nums]">
                  {formatMoney(expense.total, expense.currency)}
                </span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Receipt photo */}
      {photoEnabled && (
        <div className="mb-6">
          <h2 className="font-sans text-xs font-medium text-muted mb-2.5 px-1">
            Photo
          </h2>
          {photoLoading && (
            <div className="border border-hairline rounded-2xl w-full aspect-[4/3] flex items-center justify-center bg-surface">
              <p className="text-muted text-xs">Loading photo…</p>
            </div>
          )}
          {!photoLoading && photoUrl && (
            <img
              src={photoUrl}
              alt="Receipt photo"
              className="w-full border border-hairline rounded-2xl object-contain max-h-80"
            />
          )}
        </div>
      )}

      {/* Undo button */}
      {expense.undoable && (
        <button
          type="button"
          onClick={() => void handleUndo()}
          disabled={undoLoading}
          className="w-full py-3 bg-danger text-bone rounded-xl font-sans font-semibold text-sm active:opacity-70 transition-opacity disabled:opacity-50"
        >
          {undoLoading ? "Undoing…" : "Undo this expense"}
        </button>
      )}
    </div>
  );
}
